import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { supabase } from '../../../../lib/supabase';

export interface MonitorDevice {
    id: string;
    school_id: string;
    name: string;
    token_hash: string;
    allowed_views: string[];
    is_active: boolean;
    created_at: string;
    updated_at: string;
    last_seen_at: string | null;
}

export interface MonitorDeviceWithStatus extends MonitorDevice {
    status: 'online' | 'offline' | 'inactive';
    lastSeenMinutesAgo: number | null;
}

export interface CreateMonitorArgs {
    name: string;
    allowed_views?: string[];
}

export interface UpdateMonitorArgs {
    id: string;
    name?: string;
    allowed_views?: string[];
    is_active?: boolean;
}

export interface DeleteMonitorArgs {
    id: string;
}

// Helper function to hash token (simple SHA-256)
async function hashToken(token: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(token);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Helper function to determine monitor status
function getMonitorStatus(monitor: MonitorDevice): MonitorDeviceWithStatus {
    if (!monitor.is_active) {
        return {
            ...monitor,
            status: 'inactive',
            lastSeenMinutesAgo: null,
        };
    }

    if (!monitor.last_seen_at) {
        return {
            ...monitor,
            status: 'offline',
            lastSeenMinutesAgo: null,
        };
    }

    const lastSeen = new Date(monitor.last_seen_at);
    const now = new Date();
    const minutesAgo = Math.floor((now.getTime() - lastSeen.getTime()) / (1000 * 60));

    // Consider online if heartbeat within last 2 minutes
    const status = minutesAgo <= 2 ? 'online' : 'offline';

    return {
        ...monitor,
        status,
        lastSeenMinutesAgo: minutesAgo,
    };
}

export const monitorApi = createApi({
    reducerPath: 'monitorApi',
    baseQuery: fakeBaseQuery(),
    tagTypes: ['Monitor', 'MonitorList'],
    endpoints: (builder) => ({
        // Get all monitors for the school
        getMonitors: builder.query<MonitorDeviceWithStatus[], { schoolId: string | null }>({
            async queryFn(args) {
                try {
                    if (!args.schoolId) {
                        return { data: [] };
                    }

                    const { data, error } = await supabase
                        .from('structure_monitor_devices')
                        .select('*')
                        .eq('school_id', args.schoolId)
                        .order('created_at', { ascending: false });

                    if (error) {
                        return { error };
                    }

                    const monitorsWithStatus = (data || []).map(getMonitorStatus);

                    return { data: monitorsWithStatus };
                } catch (error: any) {
                    return { error };
                }
            },
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(({ id }) => ({ type: 'Monitor' as const, id })),
                        { type: 'MonitorList' as const, id: 'LIST' },
                    ]
                    : [{ type: 'MonitorList' as const, id: 'LIST' }],
        }),

        // Get a single monitor by ID
        getMonitor: builder.query<MonitorDeviceWithStatus | null, { id: string }>({
            async queryFn(args) {
                try {
                    const { data, error } = await supabase
                        .from('structure_monitor_devices')
                        .select('*')
                        .eq('id', args.id)
                        .single();

                    if (error) {
                        return { error };
                    }

                    if (!data) {
                        return { data: null };
                    }

                    return { data: getMonitorStatus(data) };
                } catch (error: any) {
                    return { error };
                }
            },
            providesTags: (_result, _error, args) => [{ type: 'Monitor', id: args.id }],
        }),

        // Update monitor
        updateMonitor: builder.mutation<MonitorDevice, UpdateMonitorArgs>({
            async queryFn(args) {
                try {
                    const { id, ...updates } = args;

                    const { data, error } = await supabase
                        .from('structure_monitor_devices')
                        .update({
                            ...updates,
                            updated_at: new Date().toISOString(),
                        })
                        .eq('id', id)
                        .select()
                        .single();

                    if (error) {
                        return { error };
                    }

                    return { data };
                } catch (error: any) {
                    return { error };
                }
            },
            invalidatesTags: (_result, _error, args) => [
                { type: 'Monitor', id: args.id },
                { type: 'MonitorList', id: 'LIST' },
            ],
        }),

        // Create monitor pairing code
        createMonitor: builder.mutation<{ pairing_code: string; expires_at: string; name: string }, CreateMonitorArgs>({
            async queryFn(args) {
                try {
                    // Call the existing pairing code generation function
                    const { data, error } = await supabase.rpc('fn_monitor_create_pairing_code');

                    if (error) {
                        return { error };
                    }

                    // Store the monitor name temporarily for display
                    sessionStorage.setItem(`monitor_name_${data.pairing_code}`, args.name);

                    return {
                        data: {
                            pairing_code: data.pairing_code,
                            expires_at: data.expires_at,
                            name: args.name
                        }
                    };
                } catch (error: any) {
                    return { error };
                }
            },
            invalidatesTags: [{ type: 'MonitorList', id: 'LIST' }],
        }),

        // Delete monitor
        deleteMonitor: builder.mutation<void, DeleteMonitorArgs>({
            async queryFn(args) {
                try {
                    const { error } = await supabase
                        .from('structure_monitor_devices')
                        .delete()
                        .eq('id', args.id);

                    if (error) {
                        return { error };
                    }

                    return { data: undefined };
                } catch (error: any) {
                    return { error };
                }
            },
            invalidatesTags: (_result, _error, args) => [
                { type: 'Monitor', id: args.id },
                { type: 'MonitorList', id: 'LIST' },
            ],
        }),
    }),
});

export const {
    useGetMonitorsQuery,
    useGetMonitorQuery,
    useCreateMonitorMutation,
    useUpdateMonitorMutation,
    useDeleteMonitorMutation,
} = monitorApi;
